﻿import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import { ClientBase, isAxiosError, throwException } from './ClientBase';

export enum BreedMateNotificationType {
  Generic,
  CalfReturn = 1,
  ActiveDamList,
  HerdAudit,
  Transfer,
  Registration,
}

type NotificationLink =
  | { typeId: BreedMateNotificationType.Transfer; linkData: { id: number; isAdmin?: boolean } }
  | { typeId: BreedMateNotificationType.Registration; linkData: { id: number; isAdmin?: boolean } }
  | {
      typeId: BreedMateNotificationType.CalfReturn;
      linkData: { herdId: string; year: number; isAdmin?: boolean };
    }
  | {
      typeId: BreedMateNotificationType.ActiveDamList;
      linkData: { herdId: string; year: number; isAdmin?: boolean };
    }
  | {
      typeId: BreedMateNotificationType.HerdAudit;
      linkData: { herdId: string; year: number; isAdmin?: boolean };
    };

interface NotificationBase {
  id: number;
  message: string;
  linkId: number | null;
  typeId: BreedMateNotificationType;
  forWho: number;
  isRead: boolean;
  isHidden: boolean;
  createdDate: Date;
  modifiedDate: Date;
  isNew: boolean;
}

export type BreedMateNotification = NotificationBase & NotificationLink;

export interface INotificationApiClient {
  getAll_Get(cancelToken?: CancelToken | undefined): Promise<BreedMateNotification[]>;

  get_Get(id: number, cancelToken?: CancelToken | undefined): Promise<BreedMateNotification[]>;

  update_Patch(
    id: number,
    notification: BreedMateNotification,
    cancelToken?: CancelToken | undefined
  ): Promise<BreedMateNotification>;
}

export class NotificationApiClient extends ClientBase implements INotificationApiClient {
  private instance: AxiosInstance;

  private baseUrl: string;

  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {
    super();
    this.instance = instance ? instance : axios.create();

    if (baseUrl === undefined) {
      throw new Error('baseUrl is undefined');
    }
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }

  getAll_Get(cancelToken?: CancelToken | undefined): Promise<BreedMateNotification[]> {
    let url_ = this.baseUrl + '/api/notification';
    url_ = url_.replace(/[?&]$/, '');

    let options_: AxiosRequestConfig = {
      method: 'GET',
      url: url_,
      headers: {
        Accept: 'application/json',
      },
      cancelToken,
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.instance.request(transformedOptions_))
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) =>
        this.transformResult(url_, _response, (_response: AxiosResponse) =>
          this.processGetAllGet(_response)
        )
      );
  }

  protected processGetAllGet(response: AxiosResponse): Promise<any> {
    const { status } = response;
    let _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (let k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<any>(result200);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<any>(null as any);
  }

  get_Get(id: number, cancelToken?: CancelToken | undefined): Promise<BreedMateNotification[]> {
    let url_ = this.baseUrl + '/api/notification/' + encodeURI(id.toString());
    url_ = url_.replace(/[?&]$/, '');

    let options_: AxiosRequestConfig = {
      method: 'GET',
      url: url_,
      headers: {
        Accept: 'application/json',
      },
      cancelToken,
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.instance.request(transformedOptions_))
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) =>
        this.transformResult(url_, _response, (_response: AxiosResponse) =>
          this.processGetGet(_response)
        )
      );
  }

  protected processGetGet(response: AxiosResponse): Promise<any> {
    const { status } = response;
    let _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (let k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<any>(result200);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<any>(null as any);
  }

  update_Patch(
    id: number,
    notification: BreedMateNotification,
    cancelToken?: CancelToken | undefined
  ): Promise<BreedMateNotification> {
    let url_ = `${this.baseUrl}/api/notification/${id}`;
    url_ = url_.replace(/[?&]$/, '');

    let data_ = JSON.stringify({
      isRead: notification.isRead,
      isHidden: notification.isHidden,
    });

    let options_: AxiosRequestConfig = {
      method: 'PATCH',
      url: url_,
      data: data_,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      cancelToken,
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.instance.request(transformedOptions_))
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) =>
        this.transformResult(url_, _response, (_response: AxiosResponse) =>
          this.processPatchUpdate(_response)
        )
      );
  }

  protected processPatchUpdate(response: AxiosResponse): Promise<any> {
    const { status } = response;
    let _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (let k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<any>(result200);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<any>(null as any);
  }
}
